import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Plus, Trash2, Ruler, Package } from 'lucide-react';
import { toast } from 'sonner';
import { formatErrorMessage } from '../utils/errorFormatter';

export default function PackagingOptionsDialog({ open, onOpenChange, api, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [units, setUnits] = useState([]);
  const [types, setTypes] = useState([]);
  const [newUnit, setNewUnit] = useState('');
  const [newType, setNewType] = useState('');
  const [addLoading, setAddLoading] = useState(null); // 'unit' | 'type' | null

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/packaging-options');
      setUnits(data.units || []);
      setTypes(data.types || []);
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Impossible de charger les conditionnements'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleAdd = async (category) => {
    const value = (category === 'unit' ? newUnit : newType).trim();
    if (!value) return;
    setAddLoading(category);
    try {
      await api.post('/packaging-options', { category, value });
      if (category === 'unit') setNewUnit(''); else setNewType('');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatErrorMessage(e, "Erreur lors de l'ajout"));
    } finally {
      setAddLoading(null);
    }
  };

  const handleDelete = async (option) => {
    if (!window.confirm(`Supprimer « ${option.value} » ?`)) return;
    try {
      await api.delete(`/packaging-options/${option.id}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Suppression impossible'));
    }
  };

  const renderList = (items, category, newValue, setNewValue, placeholder, IconCmp) => (
    <div className="space-y-3" data-testid={`packaging-list-${category}`}>
      <div className="flex gap-2">
        <Input
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(category); } }}
          placeholder={placeholder}
          maxLength={30}
          className="flex-1"
          data-testid={`packaging-input-${category}`}
        />
        <Button
          onClick={() => handleAdd(category)}
          disabled={!newValue.trim() || addLoading === category}
          className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
          data-testid={`packaging-add-${category}-btn`}
        >
          {addLoading === category ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Ajouter</>}
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[40vh] overflow-y-auto pr-1">
        {items.map((opt) => (
          <div
            key={opt.id}
            className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2 bg-gray-50 hover:bg-gray-100 group"
            data-testid={`packaging-item-${category}-${opt.value}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <IconCmp className="w-4 h-4 text-[#1B4332] shrink-0" />
              <span className="font-medium truncate">{opt.value}</span>
            </div>
            <button
              type="button"
              onClick={() => handleDelete(opt)}
              className="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`packaging-delete-${category}-${opt.value}`}
              aria-label="Supprimer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <p className="col-span-full text-sm text-gray-500 text-center py-6">Aucune valeur</p>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]" data-testid="packaging-options-dialog">
        <DialogHeader>
          <DialogTitle>Conditionnements</DialogTitle>
          <DialogDescription>
            Gérez les unités (KG, U, BTE…) et les types de conditionnement (colis, carton…) utilisés dans les fiches produit.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#1B4332]" />
          </div>
        ) : (
          <Tabs defaultValue="units">
            <TabsList className="grid grid-cols-2 mb-4">
              <TabsTrigger value="units" data-testid="packaging-tab-units">Unités ({units.length})</TabsTrigger>
              <TabsTrigger value="types" data-testid="packaging-tab-types">Types ({types.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="units">
              {renderList(units, 'unit', newUnit, setNewUnit, 'ex : KG, BTE, RLX…', Ruler)}
            </TabsContent>
            <TabsContent value="types">
              {renderList(types, 'type', newType, setNewType, 'ex : colis, carton, palette…', Package)}
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="packaging-close-btn">
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
